import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, fmtEur, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Building2, Factory, MapPin, FileDown, ArrowUpRight, ArrowDownLeft } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, Legend } from "recharts";

const REL_TYPES = ["Brand Owner ↔ Co-Packer", "HQ ↔ Local Site", "Distributor ↔ End Customer"];

function NodeCard({ acc, highlight, onClick, sub }) {
  return (
    <button onClick={onClick} className={`text-left border rounded-lg px-3 py-2 min-w-[170px] transition-shadow hover:shadow-md ${
      highlight ? "border-[#F5A623] bg-amber-50/60" : "border-gray-200 bg-white"}`} data-testid="network-node">
      <p className="text-sm font-semibold text-[#1B2A4A] flex items-center gap-1.5">
        <Building2 className="h-3.5 w-3.5 text-gray-400 shrink-0" />{acc.name}
      </p>
      <p className="text-[11px] text-gray-400">{sub || `${acc.type || ""} · ${acc.segment || ""}`}</p>
    </button>
  );
}

export function AccountNetwork({ accountId }) {
  const { t, canWrite } = useApp();
  const navigate = useNavigate();
  const [net, setNet] = useState(null);
  const [adding, setAdding] = useState(null);
  const [accounts, setAccounts] = useState([]);

  const load = () => api.get(`/accounts/${accountId}/network`).then((r) => setNet(r.data)).catch(() => {});
  useEffect(() => { load(); }, [accountId]);
  useEffect(() => { if (adding) api.get("/accounts?limit=500").then((r) => setAccounts(r.data)).catch(() => {}); }, [adding]);

  if (!net) return <p className="text-gray-400 text-sm">{t("loading")}</p>;

  const saveRel = async () => {
    try {
      await api.post("/relationships", { ...adding, from_account_id: accountId });
      toast.success(t("save") + " ✓");
      setAdding(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const go = (id) => id !== accountId && navigate(`/accounts/${id}`);

  return (
    <div className="space-y-5" data-testid="account-network-tab">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex flex-col items-center gap-3">
          {net.parent && (<>
            <NodeCard acc={net.parent} onClick={() => go(net.parent.id)} sub="HQ / Parent" />
            <div className="h-5 w-px bg-gray-300" />
          </>)}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {net.siblings.map((s) => <NodeCard key={s.id} acc={s} onClick={() => go(s.id)} />)}
            <NodeCard acc={net.account} highlight />
          </div>
          {net.children.length > 0 && (<>
            <div className="h-5 w-px bg-gray-300" />
            <div className="flex flex-wrap justify-center gap-3">
              {net.children.map((c) => <NodeCard key={c.id} acc={c} onClick={() => go(c.id)} sub="Subsidiary / Site" />)}
            </div>
          </>)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold flex items-center gap-1.5"><Factory className="h-4 w-4 text-gray-400" />{t("relationships")}</h3>
            {canWrite && (
              <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="add-relationship-btn"
                onClick={() => setAdding({ type: REL_TYPES[0], status: "Active" })}>
                <Plus className="h-3 w-3 mr-1" />{t("add_relationship")}
              </Button>
            )}
          </div>
          {net.relationships.length === 0 && <p className="text-xs text-gray-400">–</p>}
          <div className="space-y-2">
            {net.relationships.map((r) => (
              <div key={r.id} className="border border-gray-100 rounded-md p-3 flex items-start gap-2" data-testid="relationship-row">
                {r.direction === "out" ? <ArrowUpRight className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> : <ArrowDownLeft className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <button className="text-sm font-medium text-blue-600 hover:underline" onClick={() => go(r.other_account?.id)}>
                    {r.other_account?.name}
                  </button>
                  <p className="text-[11px] text-gray-500">{r.type} · {r.volume_pct ? `${r.volume_pct}% ${t("volume_pct")}` : ""} {r.contract_ref && `· ${r.contract_ref}`}</p>
                  {r.notes && <p className="text-[11px] text-gray-400">{r.notes}</p>}
                </div>
                <Badge className={r.status === "Active" ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" : "bg-slate-100 text-slate-500 hover:bg-slate-100"}>{r.status}</Badge>
                {canWrite && (
                  <button className="text-gray-300 hover:text-red-500" onClick={async () => { await api.delete(`/relationships/${r.id}`); load(); }} data-testid="delete-relationship-btn">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-3"><MapPin className="h-4 w-4 text-gray-400" />{t("locations")} ({net.locations.length})</h3>
          <div className="space-y-1.5">
            {net.locations.map((l) => (
              <div key={l.id} className="flex items-center gap-2 text-sm border-b border-gray-50 py-1.5" data-testid="network-location-row">
                <span className="font-medium">{l.name}</span>
                <Badge variant="outline" className="text-[10px]">{l.site_type}</Badge>
                <span className="text-gray-400 text-xs">{l.city}, {l.country}</span>
                <span className="ml-auto font-mono text-[10px] text-gray-400">{l.erp_customer_number}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={!!adding} onOpenChange={(o) => !o && setAdding(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("add_relationship")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>{t("account")}</Label>
              <Select value={adding?.to_account_id || ""} onValueChange={(v) => setAdding((a) => ({ ...a, to_account_id: v }))}>
                <SelectTrigger data-testid="rel-account-select"><SelectValue placeholder="–" /></SelectTrigger>
                <SelectContent>{accounts.filter((a) => a.id !== accountId).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label>{t("relationship_type")}</Label>
              <Select value={adding?.type} onValueChange={(v) => setAdding((a) => ({ ...a, type: v }))}>
                <SelectTrigger data-testid="rel-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{REL_TYPES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label>{t("volume_pct")}</Label>
                <Input data-testid="rel-volume-input" type="number" value={adding?.volume_pct || ""} onChange={(e) => setAdding((a) => ({ ...a, volume_pct: e.target.value }))} /></div>
              <div className="space-y-1"><Label>{t("start_date")}</Label>
                <Input type="date" value={adding?.start_date || ""} onChange={(e) => setAdding((a) => ({ ...a, start_date: e.target.value }))} /></div>
            </div>
            <div className="space-y-1"><Label>{t("contract_ref")}</Label>
              <Input value={adding?.contract_ref || ""} onChange={(e) => setAdding((a) => ({ ...a, contract_ref: e.target.value }))} /></div>
            <div className="space-y-1"><Label>{t("notes")}</Label>
              <Input value={adding?.notes || ""} onChange={(e) => setAdding((a) => ({ ...a, notes: e.target.value }))} /></div>
            <Button className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" disabled={!adding?.to_account_id}
              onClick={saveRel} data-testid="rel-save-btn">{t("save")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function AccountKpis({ accountId }) {
  const { t } = useApp();
  const [data, setData] = useState(null);
  const [locId, setLocId] = useState("all");

  useEffect(() => {
    const q = locId !== "all" ? `?location_id=${locId}` : "";
    api.get(`/accounts/${accountId}/kpis${q}`).then((r) => setData(r.data)).catch(() => {});
  }, [accountId, locId]);

  if (!data) return <p className="text-gray-400 text-sm">{t("loading")}</p>;
  const k = data.kpis;

  const exportPdf = async () => {
    const token = localStorage.getItem("lf_token");
    const res = await fetch(`${API}/accounts/${accountId}/kpis/pdf`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `Scorecard_${data.account.name}.pdf`;
    a.click();
  };

  const exportQbr = async () => {
    const token = localStorage.getItem("lf_token");
    const res = await fetch(`${API}/accounts/${accountId}/qbr-pdf`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `QBR_${data.account.name}.pdf`;
    a.click();
  };

  const kpiColor = (v, good = 95, warn = 85) => (v >= good ? "text-emerald-600" : v >= warn ? "text-[#F5A623]" : "text-red-500");

  return (
    <div className="space-y-5" data-testid="account-kpis-tab">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={locId} onValueChange={setLocId}>
          <SelectTrigger className="w-56 h-9 bg-white" data-testid="kpi-location-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("all")} {t("locations")}</SelectItem>
            {data.locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} ({l.country})</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={exportPdf} data-testid="kpi-pdf-btn">
          <FileDown className="h-3.5 w-3.5 mr-1" />{t("export_pdf")}
        </Button>
        <Button size="sm" className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={exportQbr} data-testid="qbr-pdf-btn">
          <FileDown className="h-3.5 w-3.5 mr-1" />{t("qbr_package")}
        </Button>
      </div>

      {!k ? (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-sm text-gray-400">{t("no_kpi_data")}</div>
      ) : (<>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
          {[
            [t("otif"), `${k.otif}%`, kpiColor(k.otif)],
            [t("on_time"), `${k.otd}%`, kpiColor(k.otd)],
            [t("in_full"), `${k.in_full}%`, kpiColor(k.in_full)],
            [t("fill_rate"), `${k.fill_rate}%`, kpiColor(k.fill_rate, 99, 95)],
            [t("complaint_rate"), k.complaint_rate, k.complaint_rate <= 20 ? "text-emerald-600" : k.complaint_rate <= 60 ? "text-[#F5A623]" : "text-red-500"],
            [t("health_score"), `${k.health_score}/100`, k.health_score >= 75 ? "text-emerald-600" : k.health_score >= 50 ? "text-[#F5A623]" : "text-red-500"],
            [t("forecast_accuracy"), k.forecast_accuracy != null ? `${k.forecast_accuracy}%` : "–", "text-[#1B2A4A]"],
            [t("share_of_wallet"), k.share_of_wallet != null ? `${k.share_of_wallet}%` : "–", "text-[#1B2A4A]"],
            [t("dso"), k.dso, k.dso <= 45 ? "text-emerald-600" : "text-[#F5A623]"],
            [t("days_late"), k.avg_days_late, "text-[#1B2A4A]"],
            [t("orders_12m"), k.order_count, "text-[#1B2A4A]"],
            [`${t("revenue_ytd")} (12M)`, fmtEur(k.revenue_12m), "text-[#1B2A4A]"],
          ].map(([label, val, cls]) => (
            <div key={label} className="bg-white border border-gray-200 rounded-lg p-3" data-testid={`kpi-card-${label}`}>
              <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold truncate">{label}</p>
              <p className={`text-lg font-bold font-mono ${cls}`}>{val}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-semibold mb-3">{t("otif")} / {t("on_time")} / {t("in_full")} – 12M</h3>
            <ResponsiveContainer width="100%" height={230}>
              <LineChart data={k.trend.map((m) => ({ ...m, label: m.month.slice(5) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F1F3" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis domain={[50, 100]} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend />
                <ReferenceLine y={95} stroke="#EF4444" strokeDasharray="4 4" label={{ value: "Target 95%", fontSize: 10, fill: "#EF4444" }} />
                <Line type="monotone" dataKey="otif" name="OTIF" stroke="#F5A623" strokeWidth={2.5} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="otd" name={t("on_time")} stroke="#1B2A4A" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="in_full" name={t("in_full")} stroke="#0891B2" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-semibold mb-3">{t("revenue_ytd")} – 12M ({t("vs_py")}: {k.trend_pct > 0 ? "+" : ""}{k.trend_pct}%)</h3>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={k.trend.map((m) => ({ ...m, label: m.month.slice(5) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F1F3" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip formatter={(v) => fmtEur(v)} />
                <Bar dataKey="value" name="€" fill="#1B2A4A" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold mb-3">{t("lead_time")}</h3>
          <div className="grid grid-cols-5 gap-2 text-center">
            {[["early", "≤ -1d"], ["on_time", "0d"], ["late_1_3", "+1-3d"], ["late_4_7", "+4-7d"], ["late_8plus", "+8d"]].map(([key, lbl]) => (
              <div key={key} className="border border-gray-100 rounded-md p-3">
                <p className="text-lg font-bold font-mono text-[#1B2A4A]">{k.lead_time_distribution[key]}</p>
                <p className="text-[10px] text-gray-400 uppercase">{lbl}</p>
              </div>
            ))}
          </div>
        </div>
      </>)}
    </div>
  );
}
